/**
 * useAutoRefresh.ts
 * Phase 4, Item 9 — Auto-refresh hook for schedule views.
 *
 * Usage:
 *   const { lastSyncedAt, refresh } = useAutoRefresh(loadData, 60_000);
 *
 * Behavior:
 *   - On mount, calls loaderFn() immediately and sets lastSyncedAt.
 *   - Every intervalMs thereafter, calls loaderFn() again and updates lastSyncedAt.
 *   - Pauses while document.hidden (tab not visible) to avoid wasted network
 *     requests; resumes on the next visibilitychange event.
 *   - Cleans up the interval on unmount.
 *   - `refresh()` calls loaderFn() manually and updates lastSyncedAt — useful
 *     for wiring to an explicit "Refresh" button.
 *
 * Also exports the pure helper `formatLastSynced(now, then)` so it can be
 * tested independently and reused in LastSyncedFooter.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Pure helper — exported for testing and reuse in LastSyncedFooter
// ---------------------------------------------------------------------------

/**
 * Returns a human-readable "time ago" string given two Date values.
 *
 * Boundaries:
 *   < 10 s  → "just now"
 *   10–59 s → "X seconds ago"
 *   60–119 s → "1 min ago"
 *   120 s–3599 s → "X mins ago"
 *   >= 3600 s → "X hours ago"
 *
 * @param now  — the reference moment (Date.now() equivalent)
 * @param then — the moment to describe (e.g. lastSyncedAt)
 */
export function formatLastSynced(now: Date, then: Date): string {
  const deltaMs = now.getTime() - then.getTime();
  const deltaSec = Math.max(0, Math.floor(deltaMs / 1000));

  if (deltaSec < 10) {
    return 'just now';
  }
  if (deltaSec < 60) {
    return `${deltaSec} seconds ago`;
  }
  const deltaMins = Math.floor(deltaSec / 60);
  if (deltaMins < 60) {
    return deltaMins === 1 ? '1 min ago' : `${deltaMins} mins ago`;
  }
  const deltaHours = Math.floor(deltaMins / 60);
  return deltaHours === 1 ? '1 hour ago' : `${deltaHours} hours ago`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseAutoRefreshReturn {
  /** Timestamp of the last successful loaderFn() call. Null before the first call completes. */
  lastSyncedAt: Date | null;
  /** Manually invoke loaderFn() and update lastSyncedAt. */
  refresh: () => Promise<void>;
}

/**
 * useAutoRefresh — polls `loaderFn` on mount and every `intervalMs` milliseconds.
 *
 * @param loaderFn   — async function that fetches data (the existing loadData callback)
 * @param intervalMs — polling interval in milliseconds (default 60 000 = 1 minute)
 */
export function useAutoRefresh(
  loaderFn: () => Promise<void>,
  intervalMs = 60_000,
): UseAutoRefreshReturn {
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  // Keep loaderFn in a ref so intervals always call the latest version without
  // needing to re-register the interval every time loaderFn identity changes.
  const loaderRef = useRef<() => Promise<void>>(loaderFn);
  useEffect(() => {
    loaderRef.current = loaderFn;
  }, [loaderFn]);

  const runLoader = useCallback(async () => {
    await loaderRef.current();
    setLastSyncedAt(new Date());
  }, []);

  // Track whether the interval is currently paused (tab hidden).
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedRef = useRef<boolean>(false);

  const clearExistingInterval = useCallback(() => {
    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  const startInterval = useCallback(() => {
    clearExistingInterval();
    intervalIdRef.current = setInterval(() => {
      // Check hidden again at fire time in case the state changed mid-interval.
      if (!document.hidden) {
        void runLoader();
      }
    }, intervalMs);
  }, [clearExistingInterval, intervalMs, runLoader]);

  useEffect(() => {
    // Initial load on mount.
    void runLoader();
    startInterval();

    // Pause/resume on tab visibility changes.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden — pause the interval to avoid wasted requests.
        pausedRef.current = true;
        clearExistingInterval();
      } else {
        // Tab visible again — refresh immediately then restart the interval.
        pausedRef.current = false;
        void runLoader();
        startInterval();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearExistingInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [runLoader, startInterval, clearExistingInterval]);

  // Manual refresh — called by the parent's Refresh button.
  const refresh = useCallback(async () => {
    await runLoader();
  }, [runLoader]);

  return { lastSyncedAt, refresh };
}
